import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiArrowRight,
  FiDollarSign,
  FiHome,
  FiLoader,
  FiCheckCircle,
  FiAlertCircle,
  FiChevronRight,
} from "react-icons/fi";

import {
  createWithdrawalRequest,
  resetWithdrawalRequestStatus,
} from "../../store/slices/fund";

import {
  getDashboardSummary,
} from "../../store/slices/profile";

const Withdraw = () => {
  const dispatch = useDispatch();


  const {
    withdrawalRequestLoading,
    withdrawalRequestSuccess,
    withdrawalRequestError,
  } = useSelector((state) => state.fund);


  const {
    dashboardSummary,
    getDashboardSummaryLoading,
  } = useSelector((state) => state.profile);

  /* ================= FORM STATE ================= */

  const [amount, setAmount] = useState("");
  const [validationError, setValidationError] =
    useState("");

  useEffect(() => {
    dispatch(getDashboardSummary());
  }, [dispatch]);

  const currentBalance =
    dashboardSummary?.currentBalance
    ;


  const handleAmountChange = (e) => {
    const value = e.target.value;

    setAmount(value);

    if (!value) {
      setValidationError("");
      return;
    }

    const numericValue = Number(value);
    const availableBalance = Number(
      currentBalance || 0
    );

    if (Number.isNaN(numericValue)) {
      setValidationError(
        "Please enter a valid amount."
      );
      return;
    }

    if (numericValue <= 0) {
      setValidationError(
        "Amount must be greater than 0."
      );
      return;
    }

    /* ================= MULTIPLE OF $10 ================= */

    if (numericValue % 10 !== 0) {
      setValidationError(
        "Withdrawal amount must be a multiple of $10."
      );
      return;
    }

    /* ================= BALANCE ================= */

    if (numericValue > availableBalance) {
      setValidationError(
        `Amount cannot be greater than your current balance ($ ${availableBalance.toFixed(
          2
        )}).`
      );
      return;
    }

    setValidationError("");
  };

  /* =====================================================
     SUBMIT WITHDRAWAL
  ===================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setValidationError("");

    const numericAmount = Number(amount);
    const availableBalance = Number(
      currentBalance || 0
    );

    /* ================= AMOUNT VALIDATION ================= */

    if (!amount || Number.isNaN(numericAmount)) {
      setValidationError(
        "Please enter a valid amount."
      );
      return;
    }

    if (numericAmount <= 0) {
      setValidationError(
        "Amount must be greater than 0."
      );
      return;
    }

    /* ================= WITHDRAWAL TIME ================= */

    const now = new Date();

    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    const currentTimeInMinutes =
      currentHour * 60 + currentMinute;

    // 10:00 AM
    const withdrawStartTime = 10 * 60;

    // 5:00 PM
    const withdrawEndTime = 17 * 60;

    if (
      currentTimeInMinutes < withdrawStartTime ||
      currentTimeInMinutes >= withdrawEndTime
    ) {
      setValidationError(
        "Withdrawals are available only between 10:00 AM and 5:00 PM."
      );
      return;
    }

    /* ================= MULTIPLE OF $10 ================= */

    if (numericAmount % 10 !== 0) {
      setValidationError(
        "Withdrawal amount must be a multiple of $10."
      );
      return;
    }

    /* ================= BALANCE VALIDATION ================= */

    if (numericAmount > availableBalance) {
      setValidationError(
        `Insufficient balance. Your current balance is $ ${availableBalance.toFixed(
          2
        )}.`
      );
      return;
    }

    /* ================= API REQUEST ================= */

    try {
      const payload = {
        amount: numericAmount,
      };

      console.log(
        "WITHDRAWAL REQUEST PAYLOAD:",
        payload
      );

      await dispatch(
        createWithdrawalRequest(payload)
      ).unwrap();

      /* Refresh balance */

      dispatch(getDashboardSummary());

      /* Clear amount */

      setAmount("");

    } catch (error) {
      console.error(
        "WITHDRAWAL REQUEST ERROR:",
        error
      );
    }
  };

  /* =====================================================
     RESET
  ===================================================== */

  const handleReset = () => {
    setAmount("");
    setValidationError("");

    dispatch(
      resetWithdrawalRequestStatus()
    );

    dispatch(
      getDashboardSummary()
    );
  };

  const numericAmount = Number(amount);
  const availableBalance = Number(currentBalance || 0);

  const now = new Date();
  const currentTimeInMinutes =
    now.getHours() * 60 + now.getMinutes();

  const withdrawStartTime = 10 * 60; // 10:00 AM
  const withdrawEndTime = 17 * 60;   // 5:00 PM

  const isWithdrawalTimeAllowed =
    currentTimeInMinutes >= withdrawStartTime &&
    currentTimeInMinutes < withdrawEndTime;

  const isAmountInvalid =
    !amount ||
    Number.isNaN(numericAmount) ||
    numericAmount <= 0 ||
    numericAmount % 10 !== 0 ||
    numericAmount > availableBalance;

  return (
    <div className="gi-transfer-page">

      {/* ================= BREADCRUMB ================= */}

      <div className="gi-breadcrumb">

        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>
          Fund Withdraw
        </span>

      </div>

      {/* ================= TRANSFER CARD ================= */}

      <section className="gi-transfer-card">

        {/* ================= HEADER ================= */}

        <div className="gi-transfer-header">

          <div className="gi-transfer-header-icon">
            <FiDollarSign />
          </div>

          <div>

            <h2>
              Fund Withdraw
            </h2>

          </div>

        </div>

        {/* =====================================================
            CURRENT BALANCE
        ===================================================== */}

        <div className="gi-current-balance">

          <div>

            <span>
              Current Balance
            </span>

            <strong>
              {getDashboardSummaryLoading
                ? "Loading..."
                : ` $ ${Number(
                  currentBalance
                ).toFixed(2)}`}
            </strong>

          </div>

        </div>

        {/* =====================================================
            SUCCESS
        ===================================================== */}

        {withdrawalRequestSuccess && (

          <div className="gi-transfer-success">

            <FiCheckCircle />

            <div>

              <strong>
                Withdrawal request submitted successfully
              </strong>

              <p>
                Your withdrawal request has been submitted successfully.
              </p>

            </div>

          </div>

        )}

        {/* =====================================================
            ERROR
        ===================================================== */}

        {(validationError ||
          withdrawalRequestError) && (

            <div className="gi-transfer-error">

              <FiAlertCircle />

              <span>
                {validationError ||
                  withdrawalRequestError}
              </span>

            </div>

          )}

        {/* =====================================================
            FORM
        ===================================================== */}

        {!withdrawalRequestSuccess && (

          <form
            onSubmit={handleSubmit}
            className="gi-transfer-form"
          >

            {/* ================= AMOUNT ================= */}

            <div className="gi-transfer-form-group">

              <label htmlFor="amount">

                Amount

                <span>*</span>

              </label>

              <div className="gi-transfer-input-wrapper">

                <FiDollarSign />

                <input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="Enter amount"
                  min="10"
                  max={Number(
                    currentBalance || 0
                  )}
                  step="10"
                  className="gi-transfer-input"
                  disabled={
                    withdrawalRequestLoading
                  }
                />

              </div>

              <small>
                Available balance: $
                {Number(currentBalance || 0).toFixed(2)}
              </small>

              <small
                style={{
                  display: "block",
                  marginTop: "6px",
                  color: isWithdrawalTimeAllowed ? "green" : "red",
                }}
              >
                {isWithdrawalTimeAllowed
                  ? "Withdrawal requests are available from 10:00 AM to 5:00 PM."
                  : "Withdrawal requests are available only between 10:00 AM and 5:00 PM."}
              </small>

            </div>

            {/* ================= SUBMIT ================= */}

            <button
              type="submit"
              className="gi-transfer-submit"
              disabled={
                withdrawalRequestLoading ||
                isAmountInvalid ||
                !isWithdrawalTimeAllowed ||
                !!validationError
              }
            >
              {withdrawalRequestLoading ? (
                <>
                  <FiLoader className="gi-transfer-spinner" />
                  Withdrawing...
                </>
              ) : (
                <>
                  Fund Withdraw
                  <FiArrowRight />
                </>
              )}
            </button>

          </form>

        )}

        {/* =====================================================
            AFTER SUCCESS
        ===================================================== */}

        {withdrawalRequestSuccess && (

          <div className="gi-transfer-success-actions">

            <button
              type="button"
              onClick={handleReset}
              className="gi-transfer-new-btn"
            >

              Make Another Transfer

            </button>

          </div>

        )}

      </section>

    </div>
  );
};

export default Withdraw;